import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CloseButton, Form, Modal as ModalComponent } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { createCustomer } from '../../apis/queries';
import SubmitButton from '../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../helpers/validation';
import DisplayFieldErrors from '../../utils/DisplayFormError';
import { notification, NotificationKinds } from '../../widgets/Notifications/notifications';
import { GET_CUSTOMERS_KEY } from './Customers';

interface CreateCustomerModalProps {
  show: boolean;
  handleClose: () => void;
}

interface CreateCustomerFormInput {
  name: string;
  phoneNumber: string;
  customerType: string;
  isActive: boolean;
}

const CreateCustomerModal = ({ show, handleClose }: CreateCustomerModalProps) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (customer: CreateCustomerFormInput) => {
      return createCustomer(customer);
    },
    onSuccess: data => {
      const { data: newCustomer } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `CUSTOMER CREATED`,
        subtitle: `${newCustomer?.name} has been added`
      });

      handleClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: GET_CUSTOMERS_KEY });
    }
  });

  const loading = mutation?.isPending;

  const methods = useForm<CreateCustomerFormInput>({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      phoneNumber: '',
      customerType: '',
      isActive: true
    }
  });

  const {
    getValues,
    register,
    formState: { isValid, errors }
  } = methods;

  const onCreateCustomer = () => {
    const fields = getValues();
    mutation?.mutate(fields);
  };

  return (
    <ModalComponent show={show} onHide={handleClose} centered>
      <ModalComponent.Header>
        <h4 className="card-header-title">New Customer</h4>
        <CloseButton onClick={handleClose} />
      </ModalComponent.Header>
      <ModalComponent.Body className="overflow-auto d-flex flex-column flex-grow-1">
        <Form>
          <div className="form-group">
            <Form.Label htmlFor="name">Full Name</Form.Label>
            <Form.Control
              type="text"
              id="name"
              placeholder="John Doe"
              {...register('name', {
                required: true,
                validate: value => value.trim() !== '' || 'Nickname cannot be empty',
                pattern: {
                  value: /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/,
                  message: 'Full name should contain only alphabets and numbers'
                }
              })}
              onInput={e => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^a-zA-Z0-9\s]/g, '');
              }}
              autoFocus
            />
            {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="phoneNumber">Phone Number</Form.Label>
            <Form.Control
              type="tel"
              id="phoneNumber"
              placeholder="09065068997"
              {...register('phoneNumber', {
                required: 'Phone number is required',
                pattern: {
                  value: /^[0-9]+$/,
                  message: 'Phone number must contain only digits'
                },
                validate: {
                  isNotEmpty: value => value.trim() !== '' || 'Phone number cannot be empty',
                  isValidFormat: isValidPhoneNumber
                }
              })}
              autoFocus
            />
            {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="customerType">Customer Type</Form.Label>
            <Form.Select id="customerType" {...register('customerType', { required: true })}>
              <option value="Delivery">Delivery</option>
              <option value="MDP">MDP</option>
            </Form.Select>
            {errors?.customerType && <DisplayFieldErrors displayText={errors.customerType.message!} />}
          </div>

          <SubmitButton
            buttonText="Create Customer"
            loading={loading}
            onClick={onCreateCustomer}
            disabled={!isValid || loading}
          ></SubmitButton>
        </Form>
      </ModalComponent.Body>
    </ModalComponent>
  );
};

export default CreateCustomerModal;
